import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import "./A55ChannelPopup.css";

const POPUP_HOST_SELECTOR =
  ".help-chat-box.chatIn-header.help-assistantwrapp.hbc0.maximize";

const A55ChannelPopup = ({ channels, onClose }) => {
  const markerRef = useRef(null);
  const gridRef = useRef(null);
  const scrollbarTrackRef = useRef(null);
  const dragCleanupRef = useRef(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const [scrollbar, setScrollbar] = useState({ top: 0, height: 40 });

  const updateScrollbar = useCallback(() => {
    const grid = gridRef.current;
    const track = scrollbarTrackRef.current;

    if (!grid || !track) return;

    const trackHeight = track.clientHeight;
    const maxScroll = Math.max(grid.scrollHeight - grid.clientHeight, 0);
    const thumbHeight = maxScroll
      ? Math.max(40, (grid.clientHeight / grid.scrollHeight) * trackHeight)
      : trackHeight;
    const maxThumbTop = Math.max(trackHeight - thumbHeight, 0);
    const thumbTop = maxScroll
      ? (grid.scrollTop / maxScroll) * maxThumbTop
      : 0;

    setScrollbar({ top: thumbTop, height: thumbHeight });
  }, []);

  useLayoutEffect(() => {
    const target =
      markerRef.current?.closest(POPUP_HOST_SELECTOR) ||
      document.querySelector(POPUP_HOST_SELECTOR);

    if (!target) return undefined;

    target.classList.add("a55-channel-popup-host");
    setPortalTarget(target);

    return () => {
      target.classList.remove("a55-channel-popup-host");
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useLayoutEffect(() => {
    if (!portalTarget) return undefined;

    const animationFrame = window.requestAnimationFrame(updateScrollbar);
    window.addEventListener("resize", updateScrollbar);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updateScrollbar);
    };
  }, [channels.length, portalTarget, updateScrollbar]);

  useEffect(
    () => () => {
      dragCleanupRef.current?.();
    },
    []
  );

  const handleThumbPointerDown = (event) => {
    event.preventDefault();

    const grid = gridRef.current;
    const track = scrollbarTrackRef.current;
    if (!grid || !track) return;

    dragCleanupRef.current?.();

    const startY = event.clientY;
    const startScrollTop = grid.scrollTop;
    const maxScroll = Math.max(grid.scrollHeight - grid.clientHeight, 0);
    const maxThumbTop = Math.max(track.clientHeight - scrollbar.height, 0);

    const handlePointerMove = (moveEvent) => {
      if (!maxThumbTop) return;
      grid.scrollTop =
        startScrollTop +
        ((moveEvent.clientY - startY) / maxThumbTop) * maxScroll;
    };

    const stopDragging = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopDragging);
      dragCleanupRef.current = null;
    };

    dragCleanupRef.current = stopDragging;
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDragging);
  };

  const handleTrackPointerDown = (event) => {
    if (event.target !== event.currentTarget) return;

    const grid = gridRef.current;
    const track = scrollbarTrackRef.current;
    if (!grid || !track) return;

    const trackRect = track.getBoundingClientRect();
    const maxThumbTop = Math.max(track.clientHeight - scrollbar.height, 0);
    const requestedTop = Math.min(
      Math.max(event.clientY - trackRect.top - scrollbar.height / 2, 0),
      maxThumbTop
    );
    const maxScroll = Math.max(grid.scrollHeight - grid.clientHeight, 0);

    grid.scrollTop = maxThumbTop
      ? (requestedTop / maxThumbTop) * maxScroll
      : 0;
  };

  return (
    <>
      <span ref={markerRef} className="a55-channel-popup-marker" aria-hidden="true" />

      {portalTarget &&
        ReactDOM.createPortal(
          <div
            className="a55-channel-popup-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="a55-channel-popup-title"
          >
            <div className="a55-channel-popup-card">
              <div className="a55-channel-popup-header">
                <div>
                  <h2 id="a55-channel-popup-title">Channels in your plan</h2>
                  <p>{channels.length} Channels included</p>
                </div>
                <button
                  type="button"
                  className="a55-channel-popup-close"
                  aria-label="Close app list"
                  onClick={onClose}
                >
                  ×
                </button>
              </div>

              <div className="a55-channel-popup-list">
                <div
                  ref={gridRef}
                  className="a55-channel-popup-grid"
                  onScroll={updateScrollbar}
                >
                  {channels.map((channel, index) => (
                    <div
                      className="a55-channel-popup-item"
                      key={`${channel?.channelName || "app"}-${index}`}
                    >
                      {channel?.imageLink && (
                        <img
                          src={channel.imageLink}
                          alt={channel?.channelName || "Included app"}
                        />
                      )}
                      <span>{channel?.channelName || "Included app"}</span>
                    </div>
                  ))}
                </div>

                <div
                  ref={scrollbarTrackRef}
                  className="a55-channel-popup-scrollbar"
                  aria-hidden="true"
                  onPointerDown={handleTrackPointerDown}
                >
                  <div
                    className="a55-channel-popup-scrollbar-thumb"
                    style={{
                      height: `${scrollbar.height}px`,
                      transform: `translateY(${scrollbar.top}px)`,
                    }}
                    onPointerDown={handleThumbPointerDown}
                  />
                </div>
              </div>
            </div>
          </div>,
          portalTarget
        )}
    </>
  );
};

export default A55ChannelPopup;
